/**
 * Frontend error handling utilities
 */

export interface FrontendError {
  message: string
  code?: string
  requestId?: string
  timestamp: string
  details?: Record<string, unknown>
  stack?: string
}

export interface ApiErrorResponse {
  error: string
  details?: Record<string, unknown>
  requestId?: string
  timestamp: string
}

/**
 * Parses API error responses and extracts meaningful information
 */
export function parseApiError(error: unknown): FrontendError {
  const timestamp = new Date().toISOString()
  
  // Handle fetch errors (network issues, etc.)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Network error. Please check your connection and try again.',
      code: 'NETWORK_ERROR',
      timestamp,
      stack: error.stack,
    }
  }

  // Handle API response errors
  if (error && typeof error === 'object' && 'error' in error) {
    const apiError = error as ApiErrorResponse
    return {
      message: apiError.error,
      requestId: apiError.requestId,
      timestamp: apiError.timestamp,
      details: apiError.details,
      code: apiError.details?.code as string,
      stack: apiError.details?.stack as string,
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      timestamp,
      stack: error.stack,
      code: 'UNKNOWN_ERROR',
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      timestamp,
      code: 'STRING_ERROR',
    }
  }

  // Fallback for unknown error types
  return {
    message: 'An unexpected error occurred. Please try again.',
    timestamp,
    code: 'UNKNOWN_ERROR',
    details: { originalError: error },
  }
}

/**
 * Logs errors with enhanced context for debugging
 */
export function logError(
  error: unknown, 
  context: {
    component?: string
    action?: string
    userId?: string
    additionalInfo?: Record<string, unknown>
  } = {}
): void {
  const parsedError = parseApiError(error)
  
  console.group(`🚨 Error in ${context.component || 'Unknown Component'}`)
  console.error('Error:', parsedError)
  console.error('Context:', context)
  console.error('Timestamp:', parsedError.timestamp)
  console.error('Request ID:', parsedError.requestId)
  
  if (parsedError.stack) {
    console.error('Stack Trace:', parsedError.stack)
  }
  
  if (parsedError.details) {
    console.error('Additional Details:', parsedError.details)
  }
  
  console.groupEnd()

    // Always send to error tracking service for full diagnostics
    // Example: Send to error tracking service
    // sendErrorToService(parsedError, context)
}

/**
 * Gets user-friendly error messages based on error codes
 */
export function getUserFriendlyMessage(error: FrontendError): string {
  switch (error.code) {
    case 'NETWORK_ERROR':
      return 'Unable to connect to the server. Please check your internet connection and try again.'
    
    case 'VALIDATION_ERROR':
      return 'Please check your input and try again.'
    
    case 'AUTHENTICATION_ERROR':
      return 'Invalid credentials. Please check your email and password.'
    
    case 'EMAIL_NOT_VERIFIED':
      return 'Please verify your email address before signing in.'
    
    case 'AUTHORIZATION_ERROR':
      return 'You do not have permission to perform this action.'
    
    case 'NOT_FOUND_ERROR':
      return 'The requested resource was not found.'
    
    case 'DATABASE_ERROR':
      return 'A database error occurred. Please try again later.'
    
    case 'RATE_LIMIT_ERROR':
      return 'Too many requests. Please wait a moment and try again.'
    
    case 'SERVER_ERROR':
      return 'A server error occurred. Please try again later.'
    
    default:
      return error.message || 'An unexpected error occurred. Please try again.'
  }
}

/**
 * Determines if an error should be shown to the user
 */
export function shouldShowErrorToUser(error: FrontendError): boolean {
  // Show all errors to users for full diagnostics
  return true
}

/**
 * Creates a standardized error object for form validation
 */
export function createValidationError(
  field: string, 
  message: string, 
  additionalInfo?: Record<string, unknown>
): FrontendError {
  return {
    message,
    code: 'VALIDATION_ERROR',
    timestamp: new Date().toISOString(),
    details: {
      field,
      ...additionalInfo,
    },
  }
}

/**
 * Enhanced error handling for async operations
 */
export async function handleAsyncError<T>(
  asyncFn: () => Promise<T>,
  context: {
    component?: string
    action?: string
    fallbackValue?: T
    showToUser?: boolean
  } = {}
): Promise<T | null> {
  try {
    return await asyncFn()
  } catch (error) {
    logError(error, context)
    
    const parsedError = parseApiError(error)
    
    if (context.showToUser && shouldShowErrorToUser(parsedError)) {
      // You might want to show a toast notification here
      console.error('User-facing error:', getUserFriendlyMessage(parsedError))
    }
    
    return context.fallbackValue || null
  }
}

/**
 * Wrapper for fetch requests with enhanced error handling
 */
export async function fetchWithErrorHandling(
  url: string, 
  options: RequestInit = {},
  context: {
    component?: string
    action?: string
  } = {}
): Promise<Response> {
  try {
    const response = await fetch(url, options)
    
    if (!response.ok) {
      let errorData: ApiErrorResponse
      try {
        errorData = await response.json()
      } catch {
        // If response is not JSON, create a generic error
        errorData = {
          error: `HTTP ${response.status}: ${response.statusText}`,
          timestamp: new Date().toISOString(),
        }
      }
      
      const frontendError = parseApiError(errorData)
      logError(frontendError, context)
      throw frontendError
    }
    
    return response
  } catch (error) {
    logError(error, context)
    throw error
  }
}
